import React, { useState, useEffect } from "react";
import Api from "../../components/api/Api";
import { Col, Container, Row } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Notification, { showToast } from "../../../components/common/notification/Notification";
import { useNavigate, useParams } from "react-router-dom";
import Spinner from "../../../components/common/spinner/Spinner";
import SingleDetailHeader from "../../components/singleDetail/SingleDetailHeader";
import BreadCrumb from "../../components/breadcrumb/BreadCrumb";

const EditPartnerLead = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [initialData, setInitialData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const getLeadApi = Api({
    url: `admin/service/lead/${id}`,
  });

  const editLeadApi = Api({
    url: `admin/service/lead/edit`,
  });

  const getPartnersApi = Api({
    url: "admin/associate/partners",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch partners for the dropdown
        const payload = {
          page_number: 1,
          page_size: 100,
          order_by: "ASC",
          keyword_search: ""
        };
        const partnersResponse = await getPartnersApi.postData(payload);
        let fetchedPartners = [];
        if (partnersResponse?.data?.associatedPartners) {
          fetchedPartners = partnersResponse.data.associatedPartners;
        } else if (partnersResponse?.data?.associatedPartner) {
          fetchedPartners = Array.isArray(partnersResponse.data.associatedPartner) ? partnersResponse.data.associatedPartner : [partnersResponse.data.associatedPartner];
        }
        setPartners(fetchedPartners);

        // Fetch lead details
        const leadResponse = await getLeadApi.fetchDataBySingleId();
        if (leadResponse?.data?.serviceLead) {
          const lead = leadResponse.data.serviceLead;
          setInitialData({
            first_name: lead.first_name || "",
            last_name: lead.last_name || "",
            mobile_number: lead.mobile_number || "",
            email: lead.email || "",
            city: lead.city || "",
            state: lead.state || "",
            partnerId: lead.associated_partner?.id || "",
            status: lead.status || "PENDING",
          });
        } else {
          showToast("Failed to fetch lead details", { type: "error" });
          navigate("/partner-leads");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        showToast("Error loading data", { type: "error" });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);

      const data = {
        id: Number(id),
        first_name: values.first_name,
        last_name: values.last_name,
        mobile_number: values.mobile_number,
        email: values.email,
        city: values.city,
        state: values.state,
        associated_partner: {
          id: Number(values.partnerId)
        },
        status: values.status
      };

      const response = await editLeadApi.putData(data);
      if (response && response.data && response.data.statusType === "SUCCESS") {
        showToast("Partner Lead updated successfully", { type: "success" });
        setTimeout(() => {
          navigate("/partner-leads");
        }, 3000);
      } else {
        showToast(response?.data?.text || "Failed to update lead", { type: "error" });
      }
    } catch (error) {
      console.error(error);
      showToast("Error Occurred", { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const breadcrumbItems = [
    { label: "", link: "/admin", icon: "fa-solid fa-house" },
    { label: "Partner Network", link: "/partners" },
    { label: "Partner Leads", link: "/partner-leads" },
    { label: "Edit Partner Lead", link: "", active: true },
  ];

  if (isLoading || !initialData) {
    return (
      <Container fluid style={{ marginTop: "2rem", display: "flex", justifyContent: "center" }}>
        <Spinner loading={true} />
      </Container>
    );
  }

  return (
    <>
      <Notification />
      <Container fluid style={{ marginTop: "2rem", padding: "0rem 4rem" }}>
        <BreadCrumb items={breadcrumbItems} />
        <Row>
          <Col lg={12} md={12} sm={12}>
            <SingleDetailHeader
              header="Edit Partner Lead"
              description="Update partner lead details here."
            />
            <Formik initialValues={initialData} onSubmit={handleSubmit} enableReinitialize>
              {({ isSubmitting }) => (
                <Form>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="first_name" className="form-label">First Name</label>
                        <Field type="text" className="form-control" id="first_name" placeholder="Enter first name" name="first_name" required />
                        <ErrorMessage name="first_name" component="div" className="text-danger" />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="last_name" className="form-label">Last Name</label>
                        <Field type="text" className="form-control" id="last_name" placeholder="Enter last name" name="last_name" required />
                        <ErrorMessage name="last_name" component="div" className="text-danger" />
                      </div>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="mobile_number" className="form-label">Mobile Number</label>
                        <Field type="text" className="form-control" id="mobile_number" placeholder="Enter mobile number" name="mobile_number" required />
                        <ErrorMessage name="mobile_number" component="div" className="text-danger" />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label">Email Address</label>
                        <Field type="email" className="form-control" id="email" placeholder="Enter email address" name="email" required />
                        <ErrorMessage name="email" component="div" className="text-danger" />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="city" className="form-label">City</label>
                        <Field type="text" className="form-control" id="city" placeholder="Enter city" name="city" required />
                        <ErrorMessage name="city" component="div" className="text-danger" />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="state" className="form-label">State</label>
                        <Field type="text" className="form-control" id="state" placeholder="Enter state" name="state" required />
                        <ErrorMessage name="state" component="div" className="text-danger" />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="partnerId" className="form-label">Associated Partner</label>
                        <Field as="select" className="form-control form-select" id="partnerId" name="partnerId" required>
                          <option value="">Select Partner</option>
                          {partners.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage name="partnerId" component="div" className="text-danger" />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="status" className="form-label">Status</label>
                        <Field as="select" className="form-control form-select" id="status" name="status" required>
                          <option value="PENDING">Pending</option>
                          <option value="INPROGRESS">In Progress</option>
                          <option value="COMPLETED">Completed</option>
                          <option value="REJECTED">Rejected</option>
                        </Field>
                        <ErrorMessage name="status" component="div" className="text-danger" />
                      </div>
                    </Col>
                  </Row>

                  <button type="submit" className="btn btn-primary mt-4" style={{ marginBottom: "10rem" }} disabled={isSubmitting}>
                    {isSubmitting ? <Spinner loading={true} /> : "Update Partner Lead"}
                  </button>
                </Form>
              )}
            </Formik>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default EditPartnerLead;
